import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastPosition, toast } from 'react-toastify';
import { Alert } from '@mui/material';

interface ToastProps {
  preventAutoClose?: boolean;
  color?: 'error' | 'info' | 'success' | 'warning' | undefined;
  position?: ToastPosition;
}

const useToast = () => {
  const location = useLocation();

  useEffect(() => {
    toast.dismiss();
  }, [location]);

  const pushToast = (
    message: string | JSX.Element,
    toastProps?: ToastProps
  ) => {
    const {
      preventAutoClose,
      position = 'top-right',
      color
    } = toastProps || {};

    // Dismiss all existing toasts
    toast.dismiss();

    return toast(
      ({ closeToast }) => (
        <Alert color={color || 'success'} onClose={closeToast}>
          {message}
        </Alert>
      ),
      { position, ...(preventAutoClose && { autoClose: false }) }
    );
  };

  const pushErrorToast = (
    message: string | JSX.Element,
    toastProps?: ToastProps
  ) => {
    const { preventAutoClose, position = 'top-right' } = toastProps || {};

    // Dismiss all existing toasts
    toast.dismiss();

    return toast(
      ({ closeToast }) => (
        <Alert color={'error'} onClose={closeToast} severity='error'>
          {message || 'Something went wrong'}
        </Alert>
      ),
      { position, ...(preventAutoClose && { autoClose: false }) }
    );
  };
  const pushInfoToast = (
    message: string | JSX.Element,
    toastProps?: ToastProps
  ) => {
    const { preventAutoClose, position = 'top-right' } = toastProps || {};

    // Dismiss all existing toasts
    toast.dismiss();

    return toast(
      ({ closeToast }) => (
        <Alert color={'info'} onClose={closeToast}>
          {message}
        </Alert>
      ),
      { position, ...(preventAutoClose && { autoClose: false }) }
    );
  };

  const pushSuccessToast = (
    message: string | JSX.Element,
    toastProps?: ToastProps
  ) => {
    const { preventAutoClose, position = 'top-right' } = toastProps || {};

    // Dismiss all existing toasts
    toast.dismiss();

    return toast(
      ({ closeToast }) => (
        <Alert color={'success'} onClose={closeToast}>
          {message}
        </Alert>
      ),
      { position, ...(preventAutoClose && { autoClose: false }) }
    );
  };

  return {
    pushToast,
    pushErrorToast,
    pushSuccessToast,
    pushInfoToast
  };
};

export default useToast;
